import { useHistory, Prompt } from 'react-router-dom';
import { ReactComponent as X } from 'icons/mho-x-icon.svg';
import React from 'react';
import * as Survey from 'survey-core';
import { InstrumentTypes, ProviderAssessmentTypes } from 'constants/assessment_types';
import { shouldLoadAnswers } from 'services/AssessmentHelpers';
import FacilityUsersProxy from 'api/facilityUsers/facilityUsersProxy';
import AssessmentProxy from '../../../api/assessments/assessmentProxy';
import { AssessmentResult } from '../../../api/assessments/types';
import PatientUsersProxy from '../../../api/patientUsers/patientUsersProxy';
import { UserContext } from '../../../context/user';
import Button from '../../../global_elements/Button';
import { ButtonVariant } from '../../../global_elements/Button/variants';
import LabeledTextInput from '../../../global_elements/Inputs/TextInput/LabeledTextInput';
import { TextInputVariant } from '../../../global_elements/Inputs/TextInput/variants';
import FlexContainer from '../../../global_elements/Layouts/FlexContainer';
import { AlignVariant, DisplayVariant, JustifyVariant } from '../../../global_elements/Layouts/FlexContainer/variants';
import PageLayout from '../../../global_elements/Layouts/PageLayout';
import { PageLayoutVariant } from '../../../global_elements/Layouts/PageLayout/variants';
import PopupWindow from '../../../global_elements/Layouts/PopupWindow';
import InlineText from '../../../global_elements/Text/InlineText';
import Paragraph from '../../../global_elements/Text/Paragraph';
import TertiaryHeader from '../../../global_elements/Text/TertiaryHeader';
import { FontColors, FontSizes } from '../../../global_elements/Text/variants';
import { PatientData } from '../../../interfaces/patients/patientData';
import { ProviderAssessmentCardProps } from '../../../types/cardProps';
import { AssessmentSurvey } from '../assessments/assessment';
import { AllRoutes } from '../../../constants/routes';
import ApiDataUiConverter from '../assessments/ApiDataUiConvert';

const TABLET_MODE_PIN_LENGTH = 6;

enum ContinueModes {
  Continue,
  Cancel,
}

const TabletModePage = (): React.JSX.Element => {
  const history = useHistory();
  const { activeTabletModeId, SetActiveTabletModeId } = React.useContext(UserContext);
  const { activeTabletModePatientId, SetActiveTabletModePatientId } = React.useContext(UserContext);
  const { activeTabletModeAssessments, SetActiveTabletModeAssessments } = React.useContext(UserContext);
  const [patientInfo, setPatientInfo] = React.useState<PatientData>();
  const [tabletModeAssessments, setTabletModeAssessments] = React.useState<ProviderAssessmentCardProps[]>(activeTabletModeAssessments == null ? [] : JSON.parse(activeTabletModeAssessments));
  const [tabletModeIsDirty, setTabletModeIsDirty] = React.useState<boolean>(false);
  const [tabletModeShowContinue, setTabletModeShowContinue] = React.useState<boolean>(false);
  const [tabletModeShowCompleteMessage, setTabletModeShowCompleteMessage] = React.useState<boolean>(false);
  const [showExitTabletModeWindow, setShowExitTabletModeWindow] = React.useState<boolean>(false);
  const [exitTabletModePin, setExitTabletModePin] = React.useState<string>('');
  const [exitTabletModeErrorMessage, setExitTabletModeErrorMessage] = React.useState<string>('');
  const [continueMode, setContinueMode] = React.useState<ContinueModes>(ContinueModes.Continue);
  const [submittedPixDate, setSubmittedPixDate] = React.useState<boolean>(false);
  const [assessmentErrorMessage, setAssessmentErrorMessage] = React.useState<string>('');
  const [refreshingAssessments, setRefreshingAssessments] = React.useState<boolean>(false);

  const handlePageClosePrompt = (event: any): any => {
    event.preventDefault();
    event.returnValue = '';
  };

  React.useEffect(() => {
    if (tabletModeIsDirty) {
      window.addEventListener('beforeunload', handlePageClosePrompt);
    }

    return () => {
      window.removeEventListener('beforeunload', handlePageClosePrompt);
    };
  }, [tabletModeIsDirty]);

  React.useEffect(() => {
    if (!activeTabletModePatientId) {
      return;
    }
    const patientId = parseInt(activeTabletModePatientId, 10);
    PatientUsersProxy.getPatient(
      patientId,
      (response) => {
        if (response.data) {
          const loadedPatientData: PatientData = response.data[0];
          setPatientInfo(loadedPatientData);
        }
      },
      (errorResponse) => {
        console.log(errorResponse);
      },
    );
  }, [activeTabletModePatientId]);

  const assessmentNumberPlusOne = (assessmentNumber: string): string => (parseInt(assessmentNumber, 10) + 1).toString();

  const removeCurrentAssessment = (): void => {
    if (
      tabletModeAssessments.length > 1
      && continueMode === ContinueModes.Cancel
      && tabletModeAssessments[0].instrumentTypeID === InstrumentTypes.PIX
      && tabletModeAssessments[1].instrumentTypeID === InstrumentTypes.PIX
    ) {
      // When cancelling PIX, need to remove two assessments
      tabletModeAssessments.splice(0, 2);
    } else if (continueMode === ContinueModes.Cancel || (tabletModeAssessments[0].instrumentTypeID === InstrumentTypes.PIX && !tabletModeAssessments[0].isSupplemental)) {
      // Any other assessment is cancelled OR Pix anonymous assessment is completed
      tabletModeAssessments.splice(0, 1);
    } else {
      // Refresh list of assessments using ToDo list for patient
      const patientID = activeTabletModePatientId ? parseInt(activeTabletModePatientId, 10) : -1;
      if (patientID !== -1) {
        setRefreshingAssessments(true);
        FacilityUsersProxy.getPatientAssessments(patientID)
          .then((assessmentList) => {
            if (assessmentList && assessmentList.length) {
              const currentCardProps: ProviderAssessmentCardProps[] = ApiDataUiConverter.getProviderAssessmentCardProps(assessmentList, ProviderAssessmentTypes.TODO);

              // Return a list of tablet mode assessments only found in to-do list
              if (tabletModeAssessments.length > 0) {
                // eslint-disable-next-line max-len
                const filteredAssessments = tabletModeAssessments.filter((assessment) => currentCardProps.some((todoAssessment) => todoAssessment.assessmentNumber === assessment.assessmentNumber && todoAssessment.appliedWhenID === assessment.appliedWhenID));

                // If PIX assessments remain in the todo list, add supplemental back in
                for (let i = 0; i < filteredAssessments.length; i += 1) {
                  if (filteredAssessments[i].instrumentTypeID === InstrumentTypes.PIX) {
                    filteredAssessments.splice(i + 1, 0, { ...filteredAssessments[i], assessmentNumber: assessmentNumberPlusOne(filteredAssessments[i].assessmentNumber), isSupplemental: true });
                    break;
                  }
                }

                setTabletModeAssessments(filteredAssessments);
                SetActiveTabletModeAssessments(JSON.stringify(filteredAssessments));
              }
            } else {
              setTabletModeAssessments([]);
              SetActiveTabletModeAssessments(JSON.stringify([]));
            }
          })
          .catch((errorResponse) => {
            console.log(errorResponse);
          })
          .finally(() => setRefreshingAssessments(false));
      }
    }
  };

  const tabletModeAdvance = (): void => {
    setAssessmentErrorMessage('');
    if (tabletModeAssessments.length > 1) {
      setTabletModeShowContinue(true);
    } else {
      removeCurrentAssessment();
      setTabletModeShowCompleteMessage(true);
    }
  };

  const tabletModeOnCancel = (): void => {
    setContinueMode(ContinueModes.Cancel);
    tabletModeAdvance();
  };

  const tabletModeOnComplete = async (survey: Survey.Model, options: any): Promise<void> => {
    options.showDataSaving();
    setTabletModeIsDirty(false);
    const results = Object.entries(survey.data);

    // Construct the data format for the API.
    const resultsList: AssessmentResult[] = [];
    for (let i = 0; i < results.length; i += 1) {
      if (results[i][1] !== null && results[i][1] !== undefined) {
        const resultRow = {
          patientID: tabletModeAssessments[0].patientID,
          assessmentNumber: parseInt(tabletModeAssessments[0].assessmentNumber, 10),
          variableName: results[i][0],
          dataValue: String(results[i][1]),
        };
        resultsList.push(resultRow);
      }
    }

    const promises: Promise<void>[] = [];

    try {
      promises.push(AssessmentProxy.saveAssessmentForm(resultsList));
      // For PIX assessments, need to additionally save submitted date to patient
      if (!submittedPixDate && tabletModeAssessments[0].instrumentTypeID === InstrumentTypes.PIX) {
        promises.push(AssessmentProxy.savePixSubmittedDate(resultsList, tabletModeAssessments[0].patientID));
        setSubmittedPixDate(true);
      }
      Promise.all(promises)
        .then(() => {
          options.showDataSavingSuccess();
          setContinueMode(ContinueModes.Continue);
          setTimeout(() => {
            tabletModeAdvance();
          }, 1000);
        })
        .catch((error) => {
          console.error(error);
          throw error;
        });
    } catch (error) {
      console.error(error);

      // Slight delay to show loading message briefly
      setTimeout(() => {
        options.showDataSavingError();
      }, 500);

      throw error;
    }
  };

  const tabletModeOnContinue = (): void => {
    setContinueMode(ContinueModes.Continue);
    removeCurrentAssessment();
    setTabletModeShowContinue(false);
  };

  const exitTabletMode = (): void => {
    setShowExitTabletModeWindow(true);
  };

  const verifyExitTabletModePin = (): void => {
    if (!activeTabletModeId) {
      setExitTabletModeErrorMessage('Error: No active tablet mode id specified.');
      return;
    }
    AssessmentProxy.verifyTabletModePin(activeTabletModeId, exitTabletModePin).then((r) => {
      if (!r.success) {
        setExitTabletModeErrorMessage(r.message ?? '');
      } else {
        const patientId = activeTabletModePatientId;
        setExitTabletModeErrorMessage('');
        SetActiveTabletModePatientId(null);
        SetActiveTabletModeAssessments(null);
        SetActiveTabletModeId(null);
        setTabletModeShowCompleteMessage(false);
        history.push(`${AllRoutes.PATIENT_ACCOUNTS}/${patientId}`);
      }
    });
  };

  const exitTabletModeWindow = (): React.JSX.Element => (
    <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="popup-window-wrapper">
      <PopupWindow>
        <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="popup-window-content">
          <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.CENTER} extraClasses="popup-window-header">
            <TertiaryHeader text="Tablet Mode PIN Verification" fontColor={FontColors.PRIMARY} />
            <Button
              variant={ButtonVariant.INVISIBLE}
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                setShowExitTabletModeWindow(false);
              }}
            >
              <X />
            </Button>
          </FlexContainer>
          <form className="exit-tablet-mode-pin-verification" onSubmit={verifyExitTabletModePin}>
            <LabeledTextInput
              label="PIN (6 digits)"
              placeholder="Enter the PIN set for tablet mode."
              name="exit-pin"
              value={exitTabletModePin}
              type="password"
              onChange={(e) => setExitTabletModePin(e.target.value.replace(/[^0-9]/g, '').substring(0, TABLET_MODE_PIN_LENGTH))}
              variant={TextInputVariant.PRIMARY}
              maxTextLength={TABLET_MODE_PIN_LENGTH}
              autoComplete="new-password"
              autoFocus
              inputMode="numeric"
            />
            {exitTabletModeErrorMessage !== '' && (
              <div>
                <Paragraph text={exitTabletModeErrorMessage} fontColor={FontColors.HIGH_PRIORITY} fontSize={FontSizes.REGULAR} extraClasses="error-message" bold />
              </div>
            )}
            <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.CENTER} extraClasses="popup-window-buttons">
              <Button variant={ButtonVariant.SECONDARY} onClick={() => setShowExitTabletModeWindow(false)}>
                <InlineText text="Cancel" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
              </Button>
              <Button variant={ButtonVariant.PRIMARY} disabled={exitTabletModePin.length !== TABLET_MODE_PIN_LENGTH} submit onClick={verifyExitTabletModePin} testid="submit-send-resend-button">
                <InlineText text="Exit" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
              </Button>
            </FlexContainer>
          </form>
        </FlexContainer>
      </PopupWindow>
    </FlexContainer>
  );

  if (assessmentErrorMessage !== '') {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText="Assessment Form Page" hideHeader>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="assessment-form-continue-container">
          <InlineText text={assessmentErrorMessage} fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />
          <Button
            variant={ButtonVariant.PRIMARY}
            onClick={() => {
              setContinueMode(ContinueModes.Continue);
              setTimeout(() => {
                tabletModeAdvance();
              }, 1000);
            }}
          >
            <InlineText text="Continue" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </PageLayout>
    );
  }

  if (tabletModeShowContinue) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText="Assessment Form Page" hideHeader>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="assessment-form-continue-container">
          <InlineText text="Continue to the next assessment." fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />
          <Button variant={ButtonVariant.PRIMARY} onClick={tabletModeOnContinue}>
            <InlineText text="Continue" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
        </FlexContainer>
      </PageLayout>
    );
  }

  if (tabletModeShowCompleteMessage || tabletModeAssessments.length === 0) {
    return (
      <PageLayout layout={PageLayoutVariant.PADDED} testText="Exit Tablet Mode Page" hideHeader>
        <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="exit-tablet-mode-container">
          <InlineText text="Thank you, please pass the tablet back to your provider." fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />
          <Button variant={ButtonVariant.PRIMARY} onClick={exitTabletMode}>
            <InlineText text="Exit Tablet Mode" fontColor={FontColors.BACKGROUND} fontSize={FontSizes.REGULAR} bold />
          </Button>
          {showExitTabletModeWindow && exitTabletModeWindow()}
        </FlexContainer>
      </PageLayout>
    );
  }

  if (refreshingAssessments) {
    return (
      <>
        <PageLayout layout={PageLayoutVariant.PADDED} testText="Exit Tablet Mode Page" hideHeader>
          <FlexContainer display={DisplayVariant.FLEX_COL} align={AlignVariant.START} justify={JustifyVariant.START} extraClasses="exit-tablet-mode-container">
            <InlineText text="Getting next assessment..." fontColor={FontColors.PRIMARY} fontSize={FontSizes.SUPER_EXTRA_LARGE} />
          </FlexContainer>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      {patientInfo && (
        <AssessmentSurvey
          assessmentNumber={parseInt(tabletModeAssessments[0].assessmentNumber, 10)}
          appliedWhenID={tabletModeAssessments[0].appliedWhenID}
          instrumentTypeID={tabletModeAssessments[0].instrumentTypeID}
          patientAssessmentData={{
            programCode: patientInfo.programCode,
            patientNumber: patientInfo.patientNumber,
            treatmentTypeID: patientInfo.treatmentTypeID,
            corporationID: patientInfo.corporationID,
            populationTypeID: patientInfo.populationTypeID,
            patientID: patientInfo.patientID ?? 0,
            accountID: patientInfo.accountID ?? 0,
            facilityID: patientInfo.facilityID,
          }}
          onCompleteCallback={tabletModeOnComplete}
          setIsDirtyCallback={setTabletModeIsDirty}
          onCancelCallback={tabletModeOnCancel}
          hideHeader
          isPatient
          showExtremeMessages={false}
          isPix={tabletModeAssessments[0].instrumentTypeID === InstrumentTypes.PIX}
          setAssessmentError={setAssessmentErrorMessage}
          loadAnswersFlag={shouldLoadAnswers(tabletModeAssessments[0])}
          sequenceNumber={tabletModeAssessments[0].sequenceNumber}
        />
      )}
      <Prompt when={tabletModeIsDirty} message="Are you sure you want to close the form? Incomplete assessments will be lost." />
    </>
  );
};

export default TabletModePage;
